import { parse as parseYaml, stringify as stringifyYaml } from "yaml";

const CONNECTION_FIELDS = [
  "componentId",
  "triggerOn",
  "edgeCondition",
  "isLoop",
  "maxIterations",
  "exitCondition",
];

function cleanValue(value) {
  if (
    value === undefined ||
    typeof value === "function" ||
    typeof value === "symbol"
  ) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.map(cleanValue).filter((item) => item !== undefined);
  }
  if (value instanceof Date) return value.toISOString();
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, item]) => [key, cleanValue(item)])
        .filter(([, item]) => item !== undefined)
    );
  }
  return value;
}

function normalizeConnection(connection) {
  return Object.fromEntries(
    CONNECTION_FIELDS.filter((field) => connection?.[field] !== undefined).map(
      (field) => [field, cleanValue(connection[field])]
    )
  );
}

function normalizeComponent(component) {
  return cleanValue({
    id: component.componentId ?? component.id,
    ref: component.componentRef ?? component.ref,
    type: component.type,
    name: component.name ?? component.componentData?.name,
    description: component.description,
    instruction: component.instruction,
    instructions: component.instructions,
    componentData: component.rawComponentData ?? component.componentData ?? {},
    useRawValue: component.useRawValue,
    canEvaluateFlow: component.canEvaluateFlow,
    metadataEnabled: component.metadataEnabled,
    startTimestampLabel: component.startTimestampLabel,
    endTimestampLabel: component.endTimestampLabel,
    metadataTimestampFormat: component.metadataTimestampFormat,
    next: (component.nextComponents ?? component.next ?? []).map(
      normalizeConnection
    ),
  });
}

export function formatWorkflowLogicYaml({ workflow = {}, components = [] }) {
  return stringifyYaml(
    cleanValue({
      name: workflow.name || "Untitled",
      description: workflow.description,
      extras: workflow.extras,
      useBpmnJoinBehavior: workflow.useBpmnJoinBehavior,
      components: components.map(normalizeComponent),
    }),
    { indent: 2, lineWidth: 0 }
  );
}

export function formatCanonicalWorkflowLogicYaml(yaml) {
  if (!yaml) return yaml;
  try {
    const definition = parseYaml(yaml);
    if (!definition?.spec || typeof definition.spec !== "object") return yaml;

    const spec = { ...definition.spec };
    [
      "schemaVersion",
      "canvasZoom",
      "imagePath",
      "notes",
      "viewport",
      "showNotesToggle",
      "useInlineEditing",
      "uiOnlyIconPos",
      "categories",
    ].forEach((field) => delete spec[field]);
    spec.components = (spec.components ?? []).map((component) => {
      const logic = { ...component };
      [
        "generatedTitle",
        "hasUserMarkedNotEditableAI",
        "viewData",
        "prefixLabel",
      ].forEach((field) => delete logic[field]);
      return logic;
    });

    return stringifyYaml(spec, { indent: 2, lineWidth: 0 });
  } catch (_error) {
    return yaml;
  }
}

export function getComponentLineMap(code, components = []) {
  const componentLinePattern =
    /^(\s*)-\s+(?:componentId|id):\s*["']?([^"'\s]+)["']?\s*$/;
  const matches = code
    .split("\n")
    .map((line, row) => ({ line, row }))
    .map(({ line, row }) => {
      const match = line.match(componentLinePattern);
      return match ? { id: match[2], indent: match[1].length, row } : null;
    })
    .filter(Boolean);

  if (matches.length === 0) return {};

  const componentIds = new Set(
    components
      .map((component) => component.componentId ?? component.id)
      .filter(Boolean)
  );
  const componentIndent = Math.min(
    ...matches
      .filter(({ id }) => componentIds.has(id))
      .map(({ indent }) => indent)
  );

  return Object.fromEntries(
    matches
      .filter(
        ({ id, indent }) => componentIds.has(id) && indent === componentIndent
      )
      .map(({ id, row }) => [id, row])
  );
}
